import { useState, useCallback, useEffect, useRef } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import { Library, FolderOpen, ChevronRight, FileText, HelpCircle } from 'lucide-react'
import { MainLayout } from '@/components/layouts'
import { EditableContent } from '@/components/EditableContent'
import { SearchBar, Accordion } from '@/components/ui'
import { ArticleCard } from '@/components/content'
import type { ContentCategory, ContentSubCategory, Article, EditableContent as EditableContentType } from '@/types'

interface SearchResultArticle extends Article {
  category_slug: string
  category_name: string
}

interface FaqItem {
  id: number
  question: string
  answer: string
  views_count: number
}

interface ContentProps {
  categories: ContentCategory[]
  search_results: SearchResultArticle[] | null
  query: string | null
  faq_items?: FaqItem[] | null
  editable_banner: EditableContentType | null
}

function SubCategoryCard({ category }: { category: ContentSubCategory }) {
  return (
    <Link
      href={`/content/${category.slug}`}
      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
    >
      <div className="flex items-center gap-3">
        <FileText className="w-4 h-4 text-gray-400" />
        <span className="font-medium text-gray-700 group-hover:text-tessan-primary">
          {category.name}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">
          {category.articles_count} {category.articles_count === 1 ? 'article' : 'articles'}
        </span>
        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-tessan-primary" />
      </div>
    </Link>
  )
}

function ParentCategoryCard({ category }: { category: ContentCategory }) {
  const hasChildren = category.children && category.children.length > 0

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header de la catégorie parente */}
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl font-bold text-gray-900">
              {category.name}
            </h2>
            {category.description && (
              <p className="text-sm text-gray-500 mt-1">{category.description}</p>
            )}
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold text-tessan-primary">
              {category.articles_count}
            </span>
            <p className="text-xs text-gray-500">
              {category.articles_count === 1 ? 'article' : 'articles'}
            </p>
          </div>
        </div>
      </div>

      {/* Sous-catégories */}
      {hasChildren ? (
        <div className="p-4 space-y-2">
          {category.children!.map((child) => (
            <SubCategoryCard key={child.id} category={child} />
          ))}
        </div>
      ) : (
        <Link
          href={`/content/${category.slug}`}
          className="block p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center justify-between text-tessan-primary">
            <span className="font-medium">Voir les articles</span>
            <ChevronRight className="w-5 h-5" />
          </div>
        </Link>
      )}
    </div>
  )
}

export default function Content({ categories, search_results, query, faq_items, editable_banner }: ContentProps) {
  const totalArticles = categories.reduce((sum, cat) => sum + cat.articles_count, 0)
  const [searchQuery, setSearchQuery] = useState(query || '')
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  const hasFaq = Boolean(faq_items && faq_items.length > 0)
  const [activeTab, setActiveTab] = useState<'articles' | 'faq'>('articles')
  const [faqQuery, setFaqQuery] = useState('')

  const normalizedFaqQuery = faqQuery.trim().toLowerCase()
  const filteredFaqItems = (faq_items ?? []).filter((item) => {
    if (!normalizedFaqQuery) return true
    return (
      item.question.toLowerCase().includes(normalizedFaqQuery) ||
      item.answer.toLowerCase().includes(normalizedFaqQuery)
    )
  })

  const debouncedSearch = useCallback((value: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      router.get('/content', value ? { q: value } : {}, {
        preserveState: true,
        preserveScroll: true
      })
    }, 300)
  }, [])

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const handleSearch = (value: string) => {
    setSearchQuery(value)
    debouncedSearch(value)
  }

  const isSearching = Boolean(query)

  return (
    <MainLayout
      title="Bibliothèque"
    >
      <Head title="Bibliothèque" />

      {/* Banner éditable (si existe) */}
      {editable_banner && (
        <EditableContent
          content={editable_banner}
          wrapperClassName="mb-8 -mt-8 -mx-4"
        />
      )}

      <div>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Library className="w-8 h-8 text-tessan-primary" />
            <h1 className="font-heading text-3xl font-bold text-gray-900">
              Bibliothèque
            </h1>
          </div>
          <p className="text-gray-500">
            {categories.length} {categories.length === 1 ? 'catégorie' : 'catégories'} • {totalArticles} {totalArticles === 1 ? 'article' : 'articles'}
          </p>
        </div>

        {/* Bascule Articles / FAQ */}
        {hasFaq && (
          <div className="flex items-center gap-2 mb-8">
            <button
              type="button"
              onClick={() => setActiveTab('articles')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                activeTab === 'articles'
                  ? 'bg-tessan-cream text-tessan-primary font-medium'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <Library className="w-4 h-4" />
              Articles
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('faq')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                activeTab === 'faq'
                  ? 'bg-tessan-cream text-tessan-primary font-medium'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <HelpCircle className="w-4 h-4" />
              FAQ
            </button>
          </div>
        )}

        {activeTab === 'faq' ? (
          /* Onglet FAQ */
          <div>
            {/* Barre de recherche FAQ (locale) */}
            <SearchBar
              value={faqQuery}
              onChange={setFaqQuery}
              placeholder="Rechercher une question..."
              className="mb-8"
            />

            {filteredFaqItems.length > 0 ? (
              <div className="space-y-3">
                {filteredFaqItems.map((item) => (
                  <Accordion key={item.id} title={item.question}>
                    <div className="prose prose-sm max-w-none text-gray-600 whitespace-pre-wrap">
                      {item.answer}
                    </div>
                  </Accordion>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <HelpCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Aucun résultat
                </h3>
                <p className="text-gray-500">
                  {faqQuery
                    ? `Aucune question trouvée pour "${faqQuery}"`
                    : 'Aucune question disponible pour le moment.'}
                </p>
              </div>
            )}
          </div>
        ) : (
          <>
        {/* Barre de recherche */}
        <SearchBar
          value={searchQuery}
          onChange={handleSearch}
          placeholder="Rechercher un article..."
          className="mb-8"
        />

        {/* Résultats de recherche */}
        {isSearching ? (
          search_results && search_results.length > 0 ? (
            <div>
              <p className="text-sm text-gray-500 mb-4">
                {search_results.length} résultat{search_results.length > 1 ? 's' : ''} pour "{query}"
              </p>
              <div className="space-y-4">
                {search_results.map((article) => (
                  <div key={article.id}>
                    <span className="text-xs font-medium text-tessan-primary mb-1 block">
                      {article.category_name}
                    </span>
                    <ArticleCard article={article} categorySlug={article.category_slug} />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Aucun résultat
              </h3>
              <p className="text-gray-500">
                Aucun article trouvé pour "{query}"
              </p>
            </div>
          )
        ) : (
          /* Categories */
          categories.length > 0 ? (
            <div className="space-y-6">
              {categories.map((category) => (
                <ParentCategoryCard key={category.id} category={category} />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Aucune catégorie
              </h3>
              <p className="text-gray-500">
                La bibliothèque de contenus est en cours de préparation.
              </p>
            </div>
          )
        )}
          </>
        )}
      </div>
    </MainLayout>
  )
}
